import { Component, EventEmitter, Output, HostListener, ElementRef, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { CommonModule } from '@angular/common';
import { HttpService } from '../../services/http.service';
import { ConstantsService } from '../../constants/constants.service';
import { DateFormatPipe } from '../../pipes/date-format.pipe';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import html2canvas from 'html2canvas';

@Component({
  selector: 'app-qr-scanner',
  standalone: true,
  imports: [CommonModule, ZXingScannerModule, DateFormatPipe,FormsModule],
  templateUrl: './qr-scanner.component.html',
  styleUrl: './qr-scanner.component.scss',
  animations: [
    trigger('flipCard', [
      state('default', style({
        transform: 'rotateY(0deg)'
      })),
      state('flipped', style({
        transform: 'rotateY(180deg)'
      })),
      transition('default <=> flipped', [
        animate('600ms ease-in-out')
      ])
    ])
  ]
})
export class QrScannerComponent implements OnDestroy {
  @Output() scanned = new EventEmitter<string | null>();
  @ViewChild('scannerInput', { static: false }) scannerInput!: ElementRef<HTMLInputElement>;
  
  // Scanner mode control
  scannerMode: 'barcode' | 'camera' | '' = ''; // Default to barcode scanner
  
  // Common properties
  scannerEnabled = true;
  scanCompleted = false;
  scannedResult: any = {};
  visitDetails: any = {};
  frontSide: boolean = true;
  backSide: boolean = false;
  cardState: 'default' | 'flipped' = 'default';
  isError = false;
  invalidQRError = false;
  errorMessage = '';
  showCancleModal: boolean = false;
  visitsId: any;
  checkInLoader: boolean = false;
  checkOutLoader: boolean = false;
  hostDetails: any = {};
  showAssets = false;
  imageLoading: boolean = false;
  uniqueQRresult: boolean = false;
  pendingQRresult: boolean = false;
  accessDenyRemark: string = '';
  
  // Camera scanner properties
  currentDevice: MediaDeviceInfo | undefined;
  formatsEnabled: BarcodeFormat[] = [BarcodeFormat.QR_CODE];
  cameraSupported = false;
  cameraLoading: boolean = false;
  cameraBlocked = false;
  cameraErrorMessage = '';
  
  // Barcode scanner properties
  private scanBuffer = '';
  private scanTimeout: any;
  private readonly SCAN_TIMEOUT = 100; // ms between characters for scanner input
  isMobileDevice: boolean = false;
  
  // NEW: Track if modal is ready and ignore initial input
  private isModalReady = false;
  private ignoreInputUntil = 0;
  isPrinting: boolean = false;

  constructor(
    private toastr: ToastrService, 
    private httpService: HttpService,
    private constants: ConstantsService,
    private router: Router,
    private cdRef: ChangeDetectorRef
  ) { }

ngOnInit() {
    this.checkCameraSupport();
    this.isMobileDevice =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );

    const storage = this.constants.getSession();
    if (storage) {
      this.hostDetails = storage;
    }

    this.httpService
      .getRequest(this.constants.SERVER_URLS['SETTING'] + '/declaration-status')
      .subscribe((res: any) => {
        if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
          const mode = res.result?.defaultScannerMode;

          if (this.isMobileDevice) {
            // Mobile always defaults to camera
            this.scannerMode = 'camera';
            this.startCameraScanner();
          } else if (mode === 'camera' && this.cameraSupported) {
            this.scannerMode = 'camera';
            this.startCameraScanner();
          } else {
            this.scannerMode = 'barcode';
          }
        }
        this.initScannerReady();
      });
  }

  ngOnDestroy() {
  this.releaseCameraHard();
  if (this.scanTimeout) {
    clearTimeout(this.scanTimeout);
  }
}

/** Belt-and-braces stop: disables zxing AND force-stops any video track
 *  still attached to a <video> element, in case the library's own
 *  cleanup didn't release the hardware in time. */
private releaseCameraHard(): void {
  this.scannerEnabled = false;
  this.currentDevice = undefined;

  document.querySelectorAll('video').forEach((el) => {
    const video = el as HTMLVideoElement;
    const stream = video.srcObject as MediaStream | null;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    }
  });
}

  private initScannerReady() {
    this.scanBuffer = '';
    this.ignoreInputUntil = Date.now() + 500;

    setTimeout(() => {
      this.isModalReady = true;
      if (this.scannerInput?.nativeElement) {
        this.scannerInput.nativeElement.value = '';
      }
      if (this.scannerMode === 'barcode' && !this.isMobileDevice) {
        this.focusOnScannerInput();
      }
    }, 200);
  }

  ngAfterViewInit() {
    // Clear any existing value in the scanner input
    if (this.scannerInput?.nativeElement) {
      this.scannerInput.nativeElement.value = '';
    }
    
    // Only auto-focus on desktop devices after modal is ready
    if (this.scannerMode === 'barcode' && !this.isMobileDevice && this.isModalReady) {
      setTimeout(() => {
        this.focusOnScannerInput();
      }, 100);
    }
  }

  // Toggle between camera and barcode scanner
  toggleScannerMode() {
  const leavingCamera = this.scannerMode === 'camera';
  this.scannerMode = this.scannerMode === 'barcode' ? 'camera' : 'barcode';

  // Clear any buffered data
  this.scanBuffer = '';
  if (this.scanTimeout) {
    clearTimeout(this.scanTimeout);
  }

  if (leavingCamera) {
    this.releaseCameraHard();
  }

  if (this.scannerMode === 'camera') {
    this.scannerEnabled = true;
    this.cameraLoading = true;
    this.startCameraScanner();
  } else {
    this.scannerEnabled = true;
    if (!this.isMobileDevice) {
      // Clear input field before focusing
      if (this.scannerInput?.nativeElement) {
        this.scannerInput.nativeElement.value = '';
      }
      this.focusOnScannerInput();
    }
  }
}

  // Check if camera is supported
  checkCameraSupport() {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        this.cameraSupported = videoDevices.length > 0;
      }).catch(() => {
        this.cameraSupported = false;
      });
    } else {
      this.cameraSupported = false;
    }
  }

  // Start camera scanner
  async startCameraScanner() {
  this.cameraBlocked = false;
  this.cameraErrorMessage = '';
  this.cameraLoading = true;

  if (!navigator.mediaDevices?.getUserMedia) {
    this.handleCameraError({ name: 'NotSupportedError' });
    return;
  }

  try {
    // Probe permission first, then release the camera immediately
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach(t => t.stop());

    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(d => d.kind === 'videoinput');

    if (videoDevices.length > 0) {
      this.scannerEnabled = true;
      this.currentDevice = videoDevices[0];
    } else {
      this.handleCameraError({ name: 'NotFoundError' });
    }
  } catch (err: any) {
    this.handleCameraError(err);
  }
}

private handleCameraError(err: any) {
  console.error('Camera error:', err);
  this.cameraLoading = false;
  this.releaseCameraHard();
  this.cameraBlocked = true;

  switch (err?.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      this.cameraErrorMessage =
        'Camera access is blocked. Please allow camera permission in your browser settings and try again.';
      break;
    case 'NotFoundError':
      this.cameraErrorMessage = 'No camera device found.';
      break;
    case 'NotReadableError':
      this.cameraErrorMessage = 'Camera is being used by another application.';
      break;
    default:
      this.cameraErrorMessage = 'Unable to access the camera on this device or browser.';
  }
  this.toastr.warning(this.cameraErrorMessage);
}

// Fallback: zxing's own permission event
onPermissionResponse(granted: boolean) {
  if (!granted) {
    this.handleCameraError({ name: 'NotAllowedError' });
  }
}

retryCamera() {
  this.scannerEnabled = true;
  this.startCameraScanner();
}

  // Switch to barcode scanner if camera fails
  switchToBarcodeScanner() {
  this.releaseCameraHard();
  this.scannerMode = 'barcode';
  this.focusOnScannerInput();
}

  // Focus on barcode scanner input
  focusOnScannerInput() {
    // Don't focus on mobile devices to prevent keyboard
    if (this.isMobileDevice || !this.isModalReady) {
      return;
    }
    
    if (this.scannerInput && this.scannerMode === 'barcode') {
      // Clear the input before focusing
      this.scannerInput.nativeElement.value = '';
      this.scanBuffer = '';
      this.scannerInput.nativeElement.focus();
    }
  }

  // Barcode scanner input method
  onScannerInput(event: Event) {
    // Ignore input during the initial delay period
    if (Date.now() < this.ignoreInputUntil) {
      const input = event.target as HTMLInputElement;
      input.value = '';
      return;
    }
    
    // CRITICAL: Only process if modal is ready and scanner is enabled
    if (this.scannerMode !== 'barcode' || !this.isModalReady || !this.scannerEnabled) {
      return;
    }
    
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();
    
    if (value) {
      this.handleQrCodeResult(value);
      input.value = ''; // Clear the input
    }
  }

  // Barcode scanner keydown events
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Ignore input during the initial delay period
    if (Date.now() < this.ignoreInputUntil) {
      this.scanBuffer = '';
      return;
    }
    
    // CRITICAL: Only process if modal is ready, scanner enabled, and in barcode mode
    if (this.scannerMode !== 'barcode' || 
        !this.isModalReady || 
        !this.scannerEnabled || 
        this.scanCompleted || 
        this.isError || 
        this.invalidQRError) {
      return;
    }

    // Ignore if typing in an input field (except our hidden scanner input)
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' && target.id !== 'scannerInput') {
      return;
    }
    
    // Also ignore if typing in textareas or content editable
    if (target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // Clear timeout if it exists
    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }

    // Handle Enter key (scanner usually sends Enter after scanning)
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.scanBuffer.trim()) {
        this.handleQrCodeResult(this.scanBuffer.trim());
        this.scanBuffer = '';
      }
      return;
    }

    // Handle regular characters
    if (event.key.length === 1) {
      event.preventDefault();
      this.scanBuffer += event.key;
      
      // Set timeout to clear buffer if no more input comes
      this.scanTimeout = setTimeout(() => {
        if (this.scanBuffer.trim()) {
          this.handleQrCodeResult(this.scanBuffer.trim());
        }
        this.scanBuffer = '';
      }, this.SCAN_TIMEOUT);
    }
  }

  // Barcode scanner clipboard events
  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    if (this.scannerMode !== 'barcode' || 
        !this.isModalReady || 
        !this.scannerEnabled || 
        this.scanCompleted) {
      return;
    }

    const clipboardData = event.clipboardData?.getData('text');
    if (clipboardData) {
      event.preventDefault();
      this.handleQrCodeResult(clipboardData.trim());
    }
  }

  // Handle QR code result from both methods
  handleQrCodeResult(result: string) {
    try {
      console.log('Scanner Input:', result);
      
      // Check for your special Unique QR code first
      if (result === 'UniqueVBQr') {
        this.uniqueQRresult = true;   // Set your boolean
        console.log('Unique QR detected!');
        return; // Skip further processing if needed
      } else {
        this.uniqueQRresult = false;
      }

      //Pending QR Code check
      if (result === 'PendingVBQr') {
        this.pendingQRresult = true;   // Set your boolean
        console.log('Pending QR detected!');
        return; // Skip further processing if needed
      } else {
        this.pendingQRresult = false;
      }

      // Try to parse as JSON first
      let parsedResult;
      try {
        parsedResult = JSON.parse(result);
      } catch {
        // If not JSON, treat as plain text and try to extract visitId
        if (result.includes('visitId')) {
          // Handle case where scanner might read: visitId="1703164d-ae6d-4251-9033-4e5267405de0"
          const visitIdMatch = result.match(/visitId["\s]*[:=]["\s]*([a-f0-9-]+)/i);
          if (visitIdMatch) {
            parsedResult = { visitId: visitIdMatch[1] };
          }
        } else if (this.isValidUUID(result)) {
          // If the result is a valid UUID, assume it's the visitId
          parsedResult = { visitId: result };
        } else {
          throw new Error('Invalid QR Code format');
        }
      }

      this.scannedResult = parsedResult;
      console.log('Parsed QR Code Result:', parsedResult);
      
      if (parsedResult?.visitId) {
        this.scanCompleted = true;
        this.imageLoading = true;
        this.getVisitDetail(parsedResult.visitId);
      } else {
        throw new Error('Invalid QR Code format - missing visitId');
      }

    } catch (error) {
      console.error('QR Code parsing error:', error);
      this.invalidQRError = true;
      this.toastr.error('Invalid QR Code format. Please try again.');
    }
  }

  // Helper method to validate UUID format
  private isValidUUID(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  }

  closeScanner() {
  this.isModalReady = false;
  this.releaseCameraHard();
  this.scanCompleted = false;
  this.isError = false;
  this.invalidQRError = false;
  this.errorMessage = '';
  this.scanBuffer = '';
  this.cameraLoading = false;
  if (this.scanTimeout) {
    clearTimeout(this.scanTimeout);
  }
  this.scanned.emit(null);
}

  getVisitDetail(visitId: any) {
    this.httpService.getRequest(this.constants.SERVER_URLS['VISIT_DETAILS_QR'] + visitId)
      .subscribe({
        next: (res: any) => {
          if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
            this.visitDetails = res.result;
            this.scanCompleted = true;
            this.isError = false;
            setTimeout(() => {
              this.imageLoading = false;
            }, 300);
          } else {
            this.isError = true;
            this.errorMessage = res.message;
            this.imageLoading = false;
          }
        },
        error: (err) => {
          this.isError = true;
          this.errorMessage = err?.error?.message || 'Something went wrong';
          this.imageLoading = false;
        },
      });
  }

  closeVisitorCard(){
    this.scanCompleted = false;
    this.frontSide = true;
    this.backSide = false;
    this.showAssets = false;
    this.cardState = 'default';
    this.imageLoading = false;
    this.showAssets = false;
  }

  getVisitTypeClass(visitType: string): string {
    switch (visitType) {
      case 'VIP':
        return 'vip-tag';
      case 'Interview':
        return 'interview-tag';
      case 'Business':
        return 'business-tag';
      case 'Personal':
        return 'personal-tag';
      default:
        return 'default-tag';
    }
  }

  flipCard(): void {
    if (!this.imageLoading) {
      this.frontSide = !this.frontSide;
      this.backSide = !this.backSide;
      this.cardState = this.frontSide ? 'default' : 'flipped';
    }
  }

  checkedInVisit(visitDetails: any): void {
  if (visitDetails.visitor?.isBlacklist) {
    this.toastr.warning('Cannot check in because the visitor is blacklisted.');
    return;
  }

  this.checkInLoader = true;
  console.log('Checked In Visit ID:', visitDetails.visitId);

  this.httpService.putRequest(this.constants.SERVER_URLS['CHECK_IN_VISITS'] + visitDetails.visitId, {})
    .subscribe(
      (res: any) => {
        this.checkInLoader = false;
        const msg = res?.message || 'Visitor checked in successfully';
        this.toastr.success(msg);
        this.closeScanner();
      },
      (error) => {
        this.checkInLoader = false;
        const msg = error.error?.message || 'Check-in failed';
        this.toastr.error(msg);
      }
    );
}

checkedOutVisit(visitDetails: any): void {
  if (visitDetails.visitor?.isBlacklist) {
    this.toastr.warning('Cannot check out because the visitor is blacklisted.');
    return;
  }

  this.checkOutLoader = true;
  console.log('Checked Out Visit ID:', visitDetails.visitId);

  this.httpService.putRequest(this.constants.SERVER_URLS['CHECK_OUT_VISITS'] + visitDetails.visitId, {})
    .subscribe(
      (res: any) => {
        this.checkOutLoader = false;
        const msg = res?.message || 'Visitor checked out successfully';
        this.toastr.success(msg);
        this.closeScanner();
      },
      (error) => {
        this.checkOutLoader = false;
        const msg = error.error?.message || 'Check-out failed';
        this.toastr.error(msg);
      }
    );
}

get validRemarkLength(): number {
  return this.accessDenyRemark ? this.accessDenyRemark.length : 0;
}

  onRemarkInput(): void {
  const MAX_LENGTH = 250;

  // ✅ Restrict multiple consecutive spaces — allow only single space
  this.accessDenyRemark = this.accessDenyRemark.replace(/  +/g, ' ');

  // ✅ Restrict leading space
  this.accessDenyRemark = this.accessDenyRemark.replace(/^ /, '');

  // ✅ Enforce character limit
  if (this.accessDenyRemark.length > MAX_LENGTH) {
    this.accessDenyRemark = this.accessDenyRemark.substring(0, MAX_LENGTH);
  }
}

  accessDeniedVisit(visitsId: any): void {
  const remark = this.accessDenyRemark || '';
  const validLength = remark.length;
  if (!remark.trim()) {
    this.toastr.warning('Please enter a remark to continue.');
    return;
  }

  if (validLength < 10) {
    this.toastr.warning('Please enter at least 10 valid characters.');
    return;
  }

  const apiUrl =
    this.constants.SERVER_URLS['ACCESS_DENIED_VISITS'] +
    visitsId +
    '?remark=' +
    encodeURIComponent(this.accessDenyRemark);

  this.httpService.putRequest(apiUrl, {}).subscribe({
    next: (res: any) => {
      if (res.status == this.constants.RESPONSE_CODES['SUCCESS']) {
        this.toastr.success('Access denied successfully');
        this.closeCancleModal();
        this.closeScanner();
        this.accessDenyRemark = '';
      } else {
        this.toastr.error(res.message);
      }
    },
  });
}

  openCancelModal() {
  this.closeVisitorCard();
  this.showCancleModal = true;
  this.releaseCameraHard();
}

  closeCancleModal() {
    this.showCancleModal = false;
    this.scanCompleted = true;
    // this.scannerEnabled = true;
    
    // Only refocus on desktop devices
    // if (this.scannerMode === 'barcode' && !this.isMobileDevice && this.isModalReady) {
    //   this.focusOnScannerInput();
    // }
  }

  onCancel(visitsId: any): void {
    console.log('Cancel Visit ID:', visitsId);
    this.httpService.putRequest(this.constants.SERVER_URLS['CANCEL_VISITS'] + visitsId, {}).subscribe((res: any) => {
      if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
        this.toastr.success(res.message);
        this.closeCancleModal();
        this.closeScanner();
      } else {
        this.toastr.error(res.message );
      }
    },
    (error) => {
      this.toastr.error('Failed to cancel the visit' );
    });
  }

  getStatusIcon(status: string): { icon: string; tooltip: string } {
    switch (status) {
      case 'Pending_Approvals':
        return {
          icon: 'assets/icons/pendingIcon.svg',
          tooltip: 'Pending Approval',
        };
      case 'Pending_Manager_Approvals':
        return {
          icon: 'assets/icons/pendingIcon.svg',
          tooltip: 'Pending Manager Approval',
        };
      case 'Pending_Host_Review':
        return {
          icon: 'assets/icons/pendingIcon.svg',
          tooltip: 'Pending Host Review',
        };
      case 'Checked_Out':
        return {
          icon: 'assets/icons/completed-icon.svg',
          tooltip: 'Checked Out',
        };
      case 'Scheduled':
        return { icon: 'assets/icons/upcoming-icon.svg', tooltip: 'Scheduled' };
      case 'Checked_In':
        return { icon: 'assets/icons/checked-in.svg', tooltip: 'Checked In' };
      case 'Cancelled':
        return { icon: 'assets/icons/canceled-icon.svg', tooltip: 'Cancelled' };
      default:
        return {
          icon: 'assets/icons/default-icon.png',
          tooltip: 'Unknown Status',
        };
    }
  }
 printVisitorCard() {
  this.isPrinting = true;

  const frontCardElement = document.getElementById('printFrontCard');
  const backCardElement = document.getElementById('printBackCard');
  const strips = document.querySelectorAll('.hide-strip');

  if (!frontCardElement || !backCardElement) {
    console.error('Card elements not found.');
    this.isPrinting = false;
    return;
  }

  // ✅ NEW: grab the parent [@flipCard] container and freeze it
  const flipContainer = frontCardElement.closest('[style*="transform-style"]') as HTMLElement
    ?? frontCardElement.parentElement as HTMLElement;

  const originalFlipTransform = flipContainer?.style.transform ?? '';

  const originalFrontDisplay = frontCardElement.style.display;
  const originalBackDisplay = backCardElement.style.display;
  const originalFrontHidden = frontCardElement.hasAttribute('hidden');
  const originalBackHidden = backCardElement.hasAttribute('hidden');

  // Reveal both cards
  frontCardElement.style.display = 'block';
  backCardElement.style.display = 'block';
  frontCardElement.removeAttribute('hidden');
  backCardElement.removeAttribute('hidden');

  // Reset transforms on individual cards
  frontCardElement.style.transform = 'none';
  backCardElement.style.transform = 'none';

  // ✅ NEW: also reset the parent flip container so rotateY(180deg) doesn't bleed through
  if (flipContainer) {
    flipContainer.style.transform = 'none';
  }

  strips.forEach(strip => strip.classList.add('hide-for-pdf'));

  setTimeout(() => {
    Promise.all([
      html2canvas(frontCardElement, { scale: 4, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' }),
      html2canvas(backCardElement, { scale: 4, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' })
    ]).then(([frontCanvas, backCanvas]) => {

      const frontImgData = frontCanvas.toDataURL('image/png');
      const backImgData = backCanvas.toDataURL('image/png');

      // ✅ Restore everything
      frontCardElement.style.display = originalFrontDisplay;
      backCardElement.style.display = originalBackDisplay;
      if (originalFrontHidden) frontCardElement.setAttribute('hidden', '');
      if (originalBackHidden) backCardElement.setAttribute('hidden', '');
      frontCardElement.style.transform = '';
      backCardElement.style.transform = '';
      if (flipContainer) {
        flipContainer.style.transform = originalFlipTransform; // restore flip state
      }
      strips.forEach(strip => strip.classList.remove('hide-for-pdf'));

      // ✅ Use hidden iframe instead of window.open() — fixes mobile popup block
const iframe = document.createElement('iframe');
iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
document.body.appendChild(iframe);

const iframeDoc = iframe.contentWindow?.document;
if (!iframeDoc) {
  this.toastr.error('Could not prepare print view.');
  this.isPrinting = false;
  document.body.removeChild(iframe);
  return;
}

iframeDoc.open();
iframeDoc.write(`
  <html>
  <head>
    <title>Print Visitor Card</title>
  </head>
  <body>
    <img src="${frontImgData}" style="width:54mm;height:86mm;display:block;margin:20px auto;" />
    <img src="${backImgData}" style="width:54mm;height:86mm;display:block;margin:20px auto;" />
  </body>
  </html>
`);
iframeDoc.close();

const imgs = iframeDoc.querySelectorAll('img');
let loaded = 0;
imgs.forEach(img => {
  img.onload = () => {
    loaded++;
    if (loaded === imgs.length) {
      setTimeout(() => {
        iframe.contentWindow?.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          this.isPrinting = false;
        }, 1000);
      }, 300);
    }
  };
});

    }).catch(err => {
      console.error(err);
      this.toastr.error('Failed to generate visitor card.');
      this.isPrinting = false;
    });

  }, 100);
}
onCameraStart() {
  setTimeout(() => {
    this.cameraLoading = false;
  }, 500);
}
formatTime(timeStr: string | null): string {
  if (!timeStr) return '';

  // Already formatted, e.g. "4:24 PM" — pass through as-is.
  if (/AM|PM/i.test(timeStr)) {
    return timeStr;
  }

  const [hours, minutes] = timeStr.split(':');
  const parsedHours = +hours;
  const parsedMinutes = +minutes;

  if (Number.isNaN(parsedHours) || Number.isNaN(parsedMinutes)) {
    return '';
  }

  const date = new Date();
  date.setHours(parsedHours, parsedMinutes);

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

getVisitTimeDisplay(visit: any): string {
  const duration = this.formatDurationMinutes(visit?.visitDurationMinutes);
  const formattedTime = visit?.visitTime ? this.formatTime(visit.visitTime) : '';

  const statusPart = formattedTime
    ? `Check-in ${formattedTime}`
    : 'Yet to checkin';

  return duration ? `${duration} | ${statusPart}` : statusPart;
}

formatDurationMinutes(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '';

  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  const parts: string[] = [];
  if (hrs > 0) parts.push(`${hrs} hr${hrs > 1 ? 's' : ''}`);
  if (mins > 0) parts.push(`${mins} min`);

  return parts.length ? parts.join(' ') : '0 min';
}

getOverstayDuration(visit: any): string | null {
  if (!visit?.checkInDateTime || !visit?.visitDurationMinutes) {
    return null;
  }

  const checkIn = new Date(visit.checkInDateTime);
  const scheduledEnd = new Date(checkIn.getTime() + visit.visitDurationMinutes * 60000);
  const endPoint = visit.checkOutDateTime ? new Date(visit.checkOutDateTime) : new Date();

  const diffMs = endPoint.getTime() - scheduledEnd.getTime();
  if (diffMs <= 0) {
    return null;
  }

  const diffMinutes = Math.floor(diffMs / 60000);
  return this.formatDurationMinutes(diffMinutes) || '0 min';
}
}